import moment from 'moment';
import pluralize from 'pluralize';

import { isDefined } from 'common';

import * as logger from '../logger';

export type Precision = 'seconds' | 'milliseconds';

export function isValidDate(time: Date) {
  return time instanceof Date && !isNaN(time.getTime());
}

export function isPlainIso8601DateString(dateString: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
}

export function formatTimeShort(
  time: Date | string,
  options?: { timeZone?: string; precision?: Precision }
): string {
  const date = new Date(time);
  if (!isValidDate(date)) {
    return 'Invalid Date';
  }

  const format: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    second:
      options?.precision === 'seconds' || options?.precision === 'milliseconds'
        ? '2-digit'
        : undefined,
    fractionalSecondDigits: options?.precision === 'milliseconds' ? 3 : undefined,
    hour12: true,
    timeZoneName: isDefined(options?.timeZone) ? 'short' : undefined,
  };

  if (isDefined(options?.timeZone)) {
    try {
      return new Intl.DateTimeFormat('en-US', {
        ...format,
        timeZone: options.timeZone,
      }).format(date);
    } catch (error) {
      logger.error(error, 'Error formatting with time zone');
    }
  }

  return new Intl.DateTimeFormat('en-US', format).format(date);
}

export function formatDateTimeShort(
  time: Date | string,
  options?: {
    precision?: Precision;
    timeZone?: string;
    yearFormat?: 'numeric' | '2-digit';
  }
): string {
  const date = new Date(time);
  if (!isValidDate(date)) {
    return 'Invalid Date';
  }
  const timeZone = options?.timeZone;
  const format: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: options?.yearFormat ?? 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second:
      options?.precision === 'seconds' || options?.precision === 'milliseconds'
        ? 'numeric'
        : undefined,
    fractionalSecondDigits: options?.precision === 'milliseconds' ? 3 : undefined,
    timeZoneName: timeZone ? 'short' : undefined,
  };

  if (isDefined(timeZone)) {
    try {
      return new Intl.DateTimeFormat('en-us', {
        ...format,
        timeZone,
      }).format(date);
    } catch (error) {
      // Invalid timeZone, fall back to user local timeZone
      console.error('Error formatting with timeZone', error);
    }
  }

  return new Intl.DateTimeFormat('en-us', format).format(date);
}

export function formatDateTimeFriendly(
  time: Date | string,
  options?: {
    precision?: Precision;
    timeZone?: string;
  }
): string {
  const date = new Date(time);
  if (!isValidDate(date)) {
    return 'Invalid Date';
  }
  const timeZone = options?.timeZone;
  const format: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second:
      options?.precision === 'seconds' || options?.precision === 'milliseconds'
        ? 'numeric'
        : undefined,
    fractionalSecondDigits: options?.precision === 'milliseconds' ? 3 : undefined,
    timeZoneName: undefined,
  };

  const meridiemRegex = /AM|PM$/g;

  if (isDefined(timeZone)) {
    try {
      return new Intl.DateTimeFormat('en-us', {
        ...format,
        timeZone,
      })
        .format(date)
        .replace(meridiemRegex, (match) => match.toLowerCase());
    } catch (error) {
      // Invalid timezone, fall back to user local timezone
      console.error('Error formatting with timeZone', error);
    }
  }

  return new Intl.DateTimeFormat('en-us', format)
    .format(date)
    .replace(meridiemRegex, (match) => match.toLowerCase());
}

export function formatDatePeriod(start?: Date | null, end?: Date | null): string | undefined {
  if (start && end) {
    return `${formatUsPlainDateShort(start)} to ${formatUsPlainDateShort(end)}`;
  }
  if (start) {
    return `On or after ${formatUsPlainDateShort(start)}`;
  }
  if (end) {
    return `On or before ${formatUsPlainDateShort(end)}`;
  }
}

export function formatIso8601PlainDate(time: Date | string, options?: { timeZone?: string }) {
  const date = new Date(time);
  if (!isValidDate(date)) {
    return 'Invalid Date';
  }
  const format: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  let parts: Intl.DateTimeFormatPart[];
  if (options?.timeZone) {
    try {
      parts = new Intl.DateTimeFormat('en-us', {
        ...format,
        timeZone: options.timeZone,
      }).formatToParts(date);
    } catch (error) {
      // Invalid timezone, fall back to user local timezone
      console.error('Error formatting with timeZone', error);
    }
  }

  // Fallback for no timezone or invalid timezone
  parts ??= new Intl.DateTimeFormat('en-us', format).formatToParts(date);

  return `${parts[4].value}-${parts[0].value}-${parts[2].value}`;
}

export function formatUsPlainDateShort(
  time: Date | string,
  options?: { format?: Intl.DateTimeFormatOptions; timeZone?: string }
) {
  const {
    format = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
    timeZone,
  } = options || {};
  const date = moment(time).toDate();
  if (!isValidDate(date)) {
    return 'Invalid Date';
  }
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat('en-us', {
        ...format,
        timeZone,
      }).format(date);
    } catch (error) {
      // Invalid timezone, fall back to user local timezone
      console.error('Error formatting with timeZone', error);
    }
  }
  return new Intl.DateTimeFormat('en-us', format).format(date);
}

export function formatHumanReadableDate(time: Date | string, options?: { timeZone?: string }) {
  return formatUsPlainDateShort(time, {
    format: {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    },
    ...options,
  });
}

/**
 * Formats a duration into a readable time string
 * @param options - Configuration options for formatting duration
 * @param options.duration - The duration value
 * @param options.unit - The unit of the duration (default: 'seconds')
 * @param options.format - Format style (default: 'HH:mm:ss')
 *   - 'HH:mm:ss': Zero-padded hours, minutes, seconds (e.g., "02:30:45")
 *   - 'm:ss': Minutes and zero-padded seconds (e.g., "150:45")
 *   - 'Compact': Hours and minutes, minutes and seconds, or seconds (e.g., "1h 2m")
 *   - 'Humanized': Human-readable with pluralization, automatically selects appropriate units based on duration size
 *     - >= 1 day: shows days, hours, minutes (e.g., "2 days, 5 hours, 30 min")
 *     - >= 1 hour: shows hours, minutes, seconds (e.g., "2 hours, 30 min, 45 sec")
 *     - >= 1 minute: shows minutes, seconds (e.g., "2 min, 30 sec")
 *     - < 1 minute: shows seconds, milliseconds (e.g., "5 sec, 500 ms")
 * @returns Formatted time string
 */
export function formatDuration(options: {
  duration: number;
  unit?: moment.DurationInputArg2;
  format?: 'HH:mm:ss' | 'm:ss' | 'Humanized' | 'Compact';
}): string {
  const { duration, unit = 'seconds', format = 'HH:mm:ss' } = options;
  const momentDuration = moment.duration(duration, unit);

  if (format === 'Compact') {
    const hours = Math.floor(momentDuration.asHours());
    const minutes = momentDuration.minutes();
    const seconds = momentDuration.seconds();
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  if (format === 'Humanized') {
    const parts = [];

    const formatUnit = (value: number, unit: string) => `${value} ${unit}`;

    if (momentDuration.asDays() >= 1) {
      // Show days, hours, minutes
      const totalDays = Math.floor(momentDuration.asDays());
      const hours = Math.floor(momentDuration.asHours() % 24);
      const minutes = momentDuration.minutes();

      if (totalDays > 0) {
        parts.push(formatUnit(totalDays, pluralize('day', totalDays)));
      }
      if (totalDays > 0 || hours > 0) {
        parts.push(formatUnit(hours, pluralize('hour', hours)));
      }
      parts.push(formatUnit(minutes, 'min'));
    } else if (momentDuration.asHours() >= 1) {
      // Show hours, minutes, seconds
      const totalHours = Math.floor(momentDuration.asHours());
      const minutes = momentDuration.minutes();
      const seconds = momentDuration.seconds();

      if (totalHours > 0) {
        parts.push(formatUnit(totalHours, pluralize('hour', totalHours)));
      }
      if (totalHours > 0 || minutes > 0) {
        parts.push(formatUnit(minutes, 'min'));
      }
      parts.push(formatUnit(seconds, 'sec'));
    } else if (momentDuration.asMinutes() >= 1) {
      // Show minutes, seconds
      const totalMinutes = Math.floor(momentDuration.asMinutes());
      const seconds = momentDuration.seconds();

      if (totalMinutes > 0) {
        parts.push(formatUnit(totalMinutes, 'min'));
      }
      parts.push(formatUnit(seconds, 'sec'));
    } else {
      // Show seconds, milliseconds
      const seconds = momentDuration.seconds();
      const milliseconds = Math.floor(momentDuration.milliseconds());

      if (seconds > 0) {
        parts.push(formatUnit(seconds, 'sec'));
      }
      if (milliseconds > 0 || parts.length === 0) {
        // Avoid showing things like `1 sec, 0ms`, but ensure we always fallback to `0ms` if that's all we have
        parts.push(formatUnit(milliseconds, 'ms'));
      }
    }

    return parts.join(', ');
  }

  if (format === 'm:ss') {
    const minutes = Math.floor(momentDuration.asMinutes());
    const seconds = momentDuration.seconds();
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  if (format === 'HH:mm:ss') {
    const hours = Math.floor(momentDuration.asHours());
    const minutes = momentDuration.minutes();
    const seconds = momentDuration.seconds();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  throw new Error(`Unsupported format: ${format}`);
}
